import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import { Approve } from "components/Prompts/Approve";
import { currenciesArray } from "constants/index";
import { useActionHook, usePrompt } from "hooks";
import { usePortfolioConstructionActions } from "store";
import {
  AllocationPreviewRow,
  CurrencyOption,
  PlanDealInvestor,
  PlanDealResult,
  PlannedDeal,
  PlannedDealRequest,
  PolicyAssetClass,
  SliceInvestor
} from "store/types";

import { PlanDealPrompt } from "../Plan-deal-prompt/PlanDealPrompt";

const CURRENCY_OPTIONS: CurrencyOption[] = currenciesArray.map((code) => ({ code, label: code }));
const BASE_CURRENCY = "USD";

interface UsePlannedDealsParams {
  // System + custom asset classes from the display-mode breakdown (ids can collide;
  // each carries its `type`). Custom classes are flat (no sub-classes).
  assetClasses: PolicyAssetClass[];
  investors: SliceInvestor[];
  // Called after a planned deal is created/updated/deleted, so dependent figures
  // (e.g. the allocation-drift table's nav_unfunded_planned) can refetch.
  onDealsChanged?: () => void;
}

// Owns the planned-deals list and its create/edit/delete prompt flows. Loads
// once on mount (planned deals are account-scoped, not tied to the active slice).
export const usePlannedDeals = ({ assetClasses, investors, onDealsChanged }: UsePlannedDealsParams) => {
  const openPrompt = usePrompt();
  const {
    getPlannedDealsAction,
    createPlannedDealAction,
    updatePlannedDealAction,
    deletePlannedDealAction,
    getPlannedDealAllocationPreviewAction
  } = usePortfolioConstructionActions();

  const [plannedDeals, setPlannedDeals] = useState<PlannedDeal[]>([]);

  const planDealInvestors = useMemo<PlanDealInvestor[]>(
    () => investors.map((inv) => ({ id: inv.id, name: inv.name })),
    [investors]
  );

  // Pro-rata-by-NAV split suggestion for the selected investors (no writes).
  const previewAllocations = useCallback(
    (amount: number, currency: string, investorIds: number[]): Promise<AllocationPreviewRow[]> =>
      getPlannedDealAllocationPreviewAction({ amount, currency, investors: investorIds }),
    [getPlannedDealAllocationPreviewAction]
  );

  // Memoized so the options reference is stable — `useActionHook` lists `options` in
  // its `useCallback` deps, so an inline object would recreate the action every render.
  const loadOptions = useMemo(
    () => ({
      onSuccess: (data: PlannedDeal[]) => setPlannedDeals(data),
      onReject: () => setPlannedDeals([])
    }),
    []
  );

  const { action: loadPlannedDeals } = useActionHook(getPlannedDealsAction, loadOptions);

  const onAddDeal = useCallback(async () => {
    const result = await openPrompt<{
      assetClasses: PolicyAssetClass[];
      currencies: CurrencyOption[];
      baseCurrency: string;
      investors: PlanDealInvestor[];
      previewAllocations: (amount: number, currency: string, investorIds: number[]) => Promise<AllocationPreviewRow[]>;
      saveDeal: (data: PlannedDealRequest) => Promise<void>;
      onSubmit: (result: PlanDealResult) => void;
      onCancel: () => void;
    }>({
      Component: PlanDealPrompt,
      componentType: "modal",
      disableBackdropClose: true,
      props: {
        assetClasses,
        currencies: CURRENCY_OPTIONS,
        baseCurrency: BASE_CURRENCY,
        investors: planDealInvestors,
        previewAllocations,
        saveDeal: async (data: PlannedDealRequest) => {
          const created = await createPlannedDealAction(data);
          setPlannedDeals((prev) => [...prev, created]);
        }
      }
    });
    if (!result) return;
    toast.success("Planned deal added.");
    onDealsChanged?.();
  }, [assetClasses, openPrompt, planDealInvestors, previewAllocations, createPlannedDealAction, onDealsChanged]);

  const onEditDeal = useCallback(
    async (deal: PlannedDeal) => {
      const initial: PlanDealResult = {
        name: deal.name,
        assetClassId: deal.asset_class?.id ?? "",
        assetClassType: deal.asset_class?.type ?? "system",
        subAssetClassId: deal.sub_asset_class?.id,
        investmentAmount: Number(deal.amount),
        currency: deal.currency,
        expectedDate: deal.expected_date,
        allocations: deal.investors.map((a) => ({ id: a.investor_id, amount: a.allocated_amount }))
      };
      const result = await openPrompt<{
        mode?: "create" | "edit";
        initial?: PlanDealResult;
        assetClasses: PolicyAssetClass[];
        currencies: CurrencyOption[];
        baseCurrency: string;
        investors: PlanDealInvestor[];
        previewAllocations: (amount: number, currency: string, investorIds: number[]) => Promise<AllocationPreviewRow[]>;
        saveDeal: (data: PlannedDealRequest) => Promise<void>;
        onSubmit: (result: PlanDealResult) => void;
        onCancel: () => void;
      }>({
        Component: PlanDealPrompt,
        componentType: "modal",
        disableBackdropClose: true,
        props: {
          mode: "edit",
          initial,
          assetClasses,
          currencies: CURRENCY_OPTIONS,
          baseCurrency: BASE_CURRENCY,
          investors: planDealInvestors,
          previewAllocations,
          saveDeal: async (data: PlannedDealRequest) => {
            const updated = await updatePlannedDealAction(deal.id, data);
            setPlannedDeals((prev) => prev.map((d) => (d.id === deal.id ? updated : d)));
          }
        }
      });
      if (!result) return;
      toast.success("Planned deal updated.");
      onDealsChanged?.();
    },
    [assetClasses, openPrompt, planDealInvestors, previewAllocations, updatePlannedDealAction, onDealsChanged]
  );

  const onRemoveDeal = useCallback(
    async (deal: PlannedDeal) => {
      const confirmed = await openPrompt<{
        title: string;
        description: string;
        warning?: string;
        confirmLabel?: string;
        onSubmit: (confirmed: boolean) => void;
        onCancel: () => void;
      }>({
        Component: Approve,
        componentType: "modal",
        disableBackdropClose: true,
        props: {
          title: `Delete ‘${deal.name}’?`,
          description: "This will permanently remove this planned deal.",
          confirmLabel: "Delete"
        }
      });
      if (!confirmed) return;
      await deletePlannedDealAction(deal.id);
      setPlannedDeals((prev) => prev.filter((d) => d.id !== deal.id));
      toast.success("Planned deal deleted.");
      onDealsChanged?.();
    },
    [openPrompt, deletePlannedDealAction, onDealsChanged]
  );

  useEffect(() => {
    loadPlannedDeals();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    plannedDeals,
    onAddDeal,
    onEditDeal,
    onRemoveDeal
  };
};
